/**
 * pickCompatibleBayForTech — small helper for new-ticket bay assignment.
 *
 * Replaces the four legacy `.limit(1).maybeSingle()` arbitrary-bay pickers
 * (OptiPlaceModal, useCatalystAutoAssign at INSERT time; reassignCore and
 * eodRolloverHandler server-side have their own twin). For multi-bay
 * technicians, this prefers a bay whose type satisfies the ticket's job
 * requirements (computed via bayGatingValidator) and falls back to the
 * lowest-numbered active bay if none is compatible — preserving the
 * previous "always pick a bay if the tech has any" guarantee.
 *
 * The previous arbitrary pick could place a ticket in an incompatible bay
 * for techs who own multiple bay types (e.g., 2-post + alignment-rack),
 * which then required a manual re-place via move_vehicle_location. This
 * helper avoids that round-trip on the common path; cascade and the
 * canonical RPC remain the safety nets.
 */
import { supabase } from '@/integrations/supabase/client';
import {
  computeAllowedBaysForTicket,
  isTechBayCompatible,
} from '@/utils/bayGatingValidator';

export interface JobBaySnapshot {
  allowed_bay_types_snapshot: string[] | null;
  required_capabilities_snapshot: string[] | null;
  excluded_bay_types_snapshot: string[] | null;
}

export async function pickCompatibleBayForTech(opts: {
  techId: string;
  shopId: string;
  /** Job snapshots to use for bay-gating. Optional — when omitted (e.g.,
   *  ticket not yet INSERTed and snapshots not staged), falls back to the
   *  lowest-numbered bay. Must mirror the shape stored in ticket_jobs. */
  jobs?: JobBaySnapshot[];
}): Promise<string | null> {
  const { techId, shopId, jobs } = opts;

  // Chronomancer R-BAY-9: mirror the move_vehicle_location RPC guards
  // (migration 20260424010000) — bays that are locked out or
  // parking-category must not be auto-assigned. Previously this picker
  // only filtered is_active, so OptiPlaceModal / useCatalystAutoAssign
  // could land a ticket on a broken lift or a parking bay; the RPC
  // guard then rejected the next manual move, stranding the ticket.
  const { data: bays, error } = await supabase
    .from('shop_bays')
    .select('id, bay_type, bay_number, is_locked_out, bay_category')
    .eq('assigned_tech_id', techId)
    .eq('shop_id', shopId)
    .eq('is_active', true)
    .or('is_locked_out.is.null,is_locked_out.eq.false')
    .neq('bay_category', 'parking')
    .order('bay_number', { ascending: true });

  if (error || !bays || bays.length === 0) return null;
  if (bays.length === 1) return bays[0].id;

  // Multi-bay tech: prefer a job-compatible bay if we have requirements.
  if (jobs && jobs.length > 0) {
    const requirements = computeAllowedBaysForTicket(jobs);
    if (requirements.hasRequirements) {
      const compatible = bays.find((b: any) =>
        isTechBayCompatible([b.bay_type], requirements),
      );
      if (compatible) return compatible.id;
    }
  }

  // Fallback: lowest-numbered active bay (deterministic; preserves the
  // "always return a bay if the tech has any" contract).
  return bays[0].id;
}
